/**
 * Common render model types used by both PhotoBook and Designs Editor.
 * These types act as an abstraction layer between domain models and renderers.
 */
import type { PsShadowEffect, PsStrokeEffect } from '@lite-workspace/ps-effects';
import type { WrapZone } from '@lite-workspace/text-layout';
import type {
  AdjustmentBoxParams,
  AdjustmentBinaryResource,
  BezierPath,
  BoxBevel,
  BoxGlow,
  BoxSatin,
  BoxPatternOverlay,
  BoxTextWrap,
  ObjectAssetVersions,
  SmartObjectFilter,
  TextAnchor,
  TextParagraphRun,
  TransformMatrix,
  VectorBoxParams,
  VectorFillData,
  BoxColorOverlay,
  BoxGradientOverlay,
  BoxMaskRect,
  CompoundClipBakeRecipe,
  CustomTemplateBoxBase,
  InddFirstBaselineRule,
  LayerStyleMasks,
  PsdBlendIf,
  PsdGroupComposite,
  TextAreaShapeParams,
  TextPathParams,
  TextWarpParams,
} from '@lite-workspace/dto-fotoclass-templates';

export type { TextAnchor, TransformMatrix };

/**
 * Render box types
 */
export type RenderBoxType = 'image' | 'text' | 'object' | 'background' | 'group' | 'smartObject' | 'vector' | 'adjustment';

/**
 * Rectangle with position, size and optional rotation
 */
export interface RenderRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
}

export type RenderMaskRect = BoxMaskRect;

/**
 * Shadow/stroke контракт — общий с SVG-pipeline и бэком (ps-effects lib).
 * Re-export чтобы консьюмеры canvas-engine могли импортировать эти типы
 * отсюда, как и остальной render-model, без прямой зависимости от ps-effects.
 */
export type RenderBoxShadow = PsShadowEffect;
export type RenderBoxStroke = PsStrokeEffect;
export type { PsShadowEffect, PsStrokeEffect };

/**
 * Effects that can be applied to any render box.
 *
 * Shadows/strokes структурно совпадают с ps-effects lib — та же либа рендерит
 * их в SVG для текста (фронт + бэк) и в Konva для растровых слоёв
 * (image/object/smart-object). Свечение (glows) — на одной ветке с тенью и обводкой:
 * типизировано, редактируется и доезжает до печати через book-print-service (Rust);
 * SVG-пайплайн текста (`mapBoxEffectsToSvgEffects`) его не выводит — на холсте и в
 * превью его рисует psd-render-wasm.
 *
 * Glow, bevel, satin and pattern parameters reach the live text scene unchanged; Rust owns rendering.
 * Pattern pixels use the image-resource pipeline. Marked live Color/Gradient overlays render independently;
 * unmarked legacy overlays retain their import-resolved fills.
 */
export interface RenderBoxEffects {
  bevel?: BoxBevel;
  shadows?: PsShadowEffect[];
  strokes?: PsStrokeEffect[];
  glows?: BoxGlow[];
  satin?: BoxSatin;
  patternOverlay?: BoxPatternOverlay;
  colorOverlay?: { color: string; opacity?: number; enabled?: boolean; blendMode?: string; rendering?: 'live' };
  extraColorOverlays?: BoxColorOverlay[];
  gradientOverlay?: BoxGradientOverlay;
  extraGradientOverlays?: BoxGradientOverlay[];
}

/**
 * Base properties for all render boxes
 */
export interface RenderBoxBase {
  effectReferencePoint?: { x: number; y: number };
  effectDocumentOrigin?: { x: number; y: number };
  maskRect?: RenderMaskRect;
  type: RenderBoxType;
  id: string;
  /** Baked tile only: box id of its owner, resolved per collage by `bakedTileOwners` — the commit's own answer. */
  bakedTileOwner?: string;
  rect: RenderRect;
  visible?: boolean;
  /** Spread layer (ADR 0045): print keeps a page's export area that only this layer of collage 0 reaches. */
  spreadWide?: boolean;
  /**
   * InDesign Text Wrap of this box (template `textWrap`): `resolveTextWraps` turns it into the `wrapZones` of the
   * page's texts and drops it — the engine never reads it.
   */
  textWrap?: BoxTextWrap;
  /** Designer-locked: still drawn, but excluded from hit-testing in the engine. */
  locked?: boolean;
  opacity?: number;
  fillOpacity?: number;
  blendInteriorElements?: boolean;
  /** Leaf Blend If (model `blendIf`); a group's rides in `psdGroup`. */
  blendIf?: PsdBlendIf;
  /** Leaf Knockout (model `knockout`, `knockoutBackdrop`); a group's rides in `psdGroup`. */
  knockout?: CustomTemplateBoxBase['knockout'];
  knockoutBackdrop?: CustomTemplateBoxBase['knockoutBackdrop'];
  /** Leaf Blend Clipped / Transparency Shapes Layer (`false` only); a group's ride in `psdGroup`. */
  blendClipped?: CustomTemplateBoxBase['blendClipped'];
  transparencyShapes?: CustomTemplateBoxBase['transparencyShapes'];
  layerStyleRendering?: 'live';
  layerStyleMasks?: LayerStyleMasks;
  blendMode?: string;
  effects?: RenderBoxEffects;
  /** Mirror the box content horizontally/vertically. */
  flipHorizontal?: boolean;
  flipVertical?: boolean;
}

/**
 * Image album for render box with URLs and natural dimensions
 */
export interface RenderImageAlbum {
  id?: string;
  /** Имя поля называет длинную сторону версии: THUMB 320, SMALL 640, MEDIUM 1280, LARGE 1920. */
  thumb?: string;
  small?: string;
  medium_1280?: string;
  big_1920?: string;
  image?: string;
  /** Original uploaded filename — the retoucher-facing photo identity. */
  filename?: string;
  /** Natural width of the image in pixels */
  width?: number;
  /** Natural height of the image in pixels */
  height?: number;
  /** Base64-encoded ThumbHash for blurred placeholder */
  thumb_hash?: string;
}

/**
 * Image render box
 */
export interface RenderImageBox extends RenderBoxBase {
  type: 'image';
  imageUrl?: string;
  color?: string;
  // PhotoBook specific: ImageAlbum with thumb/big URLs and natural dimensions
  imageAlbum?: RenderImageAlbum;
  // PhotoBook specific: Image position/size within container (from naturalAligns.image)
  // If not provided, image fills the entire container rect
  imageRect?: RenderRect;
  /**
   * Indicates that a face on the image overlaps the fold line.
   * When true, a warning indicator should be displayed.
   */
  hasFaceOnFold?: boolean;
  /**
   * Preview JPEG/PNG URL resolved from params.sourceRef → smart-object source.
   * Shown when no user image has been dropped (no imageAlbum / imageUrl).
   */
  previewSourceUrl?: string;
  /**
   * Base64 PNG embedded preview. Used when the slot's PSD source is a plain pixel
   * layer inside a clipping group (no smart-object → no `previewSourceUrl`). Transient —
   * present only after parse, stripped before PATCH. Renderer draws this as the full
   * fallback preview when available.
   */
  previewPixelData?: string;
  /**
   * Base64 ThumbHash of the slot preview. Persisted compact form (~30 bytes). After
   * spread reload from server `previewPixelData` is gone, so the renderer decodes this
   * to a blurred placeholder until the user drops a photo.
   */
  previewThumbHash?: string;
  /**
   * Base64 PNG alpha mask applied via destination-in after the image is drawn.
   * Inherited from the PSD clipping-group base layer — gives the photo a decorative
   * silhouette (e.g. rounded rough-edge frame). Carries either an inline data: URL
   * (legacy models, runtime re-bake) or an https URL of the uploaded alpha-only mask.
   */
  maskDataUrl?: string;
  /**
   * Id of the layer this frame is clipped to — Photoshop's clipping mask. The mask's pixels lie on THAT
   * layer's rect, so the frame slides under a standing mask and shows only what the two share; without it
   * the mask is stretched over the frame's own rect (a mask picked from the «Маски» panel).
   */
  maskBaseBoxId?: string;
  /**
   * Vector clipping paths (PSD vectorMask of the photo-frame layer). When present, the
   * renderer applies them as a Konva `clipFunc` on the image group so the photo, preview,
   * pixel-data fallback and overlays all share the rounded silhouette of the frame.
   * Path coordinates are in box-local space (relative to rect top-left).
   */
  paths?: readonly BezierPath[];
  /** Decorative bitmap frame PNG drawn over the photo. */
  frameUrl?: string;
  /**
   * Photo window inside the frame image, as fractions of the frame's own size (0..1);
   * the frame is stretched so this window maps exactly onto the box rect.
   */
  frameWindow?: { x: number; y: number; width: number; height: number };
  /**
   * The PSD template already baked this frame's outline into a sibling overlay tile, so the album
   * default must not add a second line on top of it. Its OWN stroke effect still paints. Not derivable
   * from the render box — the template kinship lives in `idFromTemplate`, which never reaches the render
   * model — so the flag travels on the wire and both renderers read it (`frameStrokeBakedIn`).
   */
  frameStrokeBaked?: boolean;
  /**
   * Corner radius of the frame in book px — an editor setting, never read from the PSD. The photo clip,
   * the frame's outlines and its inner shadows all follow the one rounded silhouette it describes.
   */
  cornerRadius?: number;
}

/** Per-run text style override — character-range [from, to) within RenderTextBox.text. */
export interface TextRun {
  from: number;
  to: number;
  fontName?: string;
  /** Layout font key of the run's own face, stamped by the host at render time (never stored); see `withRunFontRefs`. */
  fontRef?: string;
  fontSize?: number;
  /** The run's leading, canvas px — see `TEXT_LAYOUT_LINE_STACK`. */
  lineHeightPx?: number;
  color?: string;
  tracking?: number;
  fauxBold?: boolean;
  /** The run's own slant; absent — the box's (`runFace`, text-layout: read by the font resolver and the engine). */
  italic?: boolean;
  /** The run's own weight label, as the box's (`Thin` … `Black`, an InDesign one with its width word: `Bold Condensed`);
   *  absent — the box's (`runFace`, as `italic`). */
  fontWeight?: string;
  underline?: boolean;
  strikethrough?: boolean;
  fontCaps?: number;
  fontBaseline?: number;
  horizontalScale?: number;
  verticalScale?: number;
  baselineShift?: number;
  autoKerning?: boolean;
  kerning?: number;
  ligatures?: boolean;
  dLigatures?: boolean;
  // Поля ниже двигают только модельныйround-trip (inline-сессия возвращает runs в модель):
  // проекция обязана быть без потерь, иначе первый же набор в PSD-тексте стирает их из шаблона.
  language?: number;
  styleRunAlignment?: number;
  noBreak?: boolean;
  strokeColor?: string;
  fillFlag?: boolean;
  strokeFlag?: boolean;
  fillFirst?: boolean;
  outlineWidth?: number;
  yUnderline?: number;
}

/**
 * Text render box
 */
export interface RenderTextBox extends RenderBoxBase {
  type: 'text';
  text: string;
  fontName: string;
  fontWeight: string;
  italic: boolean;
  fontSize: number;
  color: string;
  /** Legacy import-resolved gradient fill; independent overlays live in `effects.gradientOverlay`. */
  fillGradient?: VectorFillData;
  /** `justify` — выключка по ширине контейнера; работает только в `block` (у point-текста рамки нет). */
  align: 'left' | 'center' | 'right' | 'justify';
  textMode?: 'block' | 'free';
  /** `block`-текст, чья рамка идёт за содержимым при вводе; снимается, когда пользователь задаёт ширину сам. */
  autoWidth?: boolean;
  /** Автоподгон размера шрифта, чтобы текст вместился в контейнер */
  autoFit?: boolean;
  /** Потолок для autoFit — максимальный fontSize при автоподгоне */
  maxFontSize?: number;
  verticalAlign?: 'top' | 'center' | 'bottom';
  // PhotoBook specific: text type for placeholder handling
  textType?: string;
  /** Vignette series quote frame with its own capacity (R10): typing is capped at this many characters (quote marks
   *  included), not at the frame edge — the planner hugs the frame to the committed quote. */
  quoteCapacity?: number;
  /** `text` holds an editor hint for an empty slot, not authored content — never write it back to the model. */
  isPlaceholder?: boolean;
  postScriptName?: string;
  /** PSD tracking value in 1/1000 em */
  tracking?: number;
  /** Line height in pixels (from PSD leading) */
  lineHeight: number;
  underline?: boolean;
  strikethrough?: boolean;
  /**
   * Photoshop faux bold — симуляция жирного через stroke на glyph path.
   * Независим от `fontWeight`. Renderer применяет `stroke=color` + ширину
   * `PSD_FAUX_BOLD_TYPED_RATIO × набранный кегль` (= `fontSize` ÷ масштаб `transform`): растяжение
   * слоя имитацию не масштабирует, это проверено в самом Photoshop (FKS-11995).
   */
  fauxBold?: boolean;
  /**
   * Photoshop "Type Options" caps (`FontCaps`): `1` = small caps, `2` = all caps.
   * Renderer applies `text.toUpperCase()`; faux small-caps tracks all caps for builtin fonts.
   */
  fontCaps?: number;
  /**
   * Photoshop "Type Options" baseline (`FontBaseline`): `1` = superscript, `2` = subscript.
   * Renderer scales fontSize ~×0.6 and shifts baseline.
   */
  fontBaseline?: number;
  /**
   * Photoshop Character panel HorizontalScale (X-glyph scale). `1` = 100%.
   * Renderer applies к advance width (для wrap) и к glyph paths (визуально).
   */
  horizontalScale?: number;
  /**
   * Photoshop Character panel VerticalScale (Y-glyph scale). `1` = 100%.
   * Renderer applies к Y-координатам paths; line-leading независимо.
   */
  verticalScale?: number;
  /** PSD LayerTextData.orientation: вертикальное письмо (CJK); отсутствие = горизонталь (дефолт). */
  orientation?: 'vertical';
  /** PSD ResourceDict.SuperscriptSize per-layer (PS-default 0.583). См. {@link fontBaseline}. */
  superscriptSize?: number;
  /** PSD ResourceDict.SuperscriptPosition per-layer (PS-default 0.333) — baseline shift up как доля от fontSize. */
  superscriptPosition?: number;
  /** PSD ResourceDict.SubscriptSize per-layer (PS-default 0.583). */
  subscriptSize?: number;
  /** PSD ResourceDict.SubscriptPosition per-layer (PS-default 0.333). */
  subscriptPosition?: number;
  /** PSD ResourceDict.SmallCapSize per-layer (PS-default 0.7) — для `fontCaps=1`. */
  smallCapSize?: number;
  /** PSD LayerTextData.useFractionalGlyphWidths — округлять advance до целого pixel при `false`. */
  useFractionalGlyphWidths?: boolean;
  /** PSD point text: the import hung the frame on Photoshop's pen box, not on the matrix origin (`TextBoxParams`). */
  frameOnPenBox?: boolean;
  /** PSD LayerTextData.antiAlias mode (0..4). FKS-9442. */
  antiAlias?: number;
  /** Point text: rect top → first baseline in em of `fontSize` (PSD anchor). Absent — ink-seated. */
  firstBaselineOffset?: number;
  /** PSD area `'ascent'` ('d' height), PSD point `'ink'`, InDesign frame its First Baseline Offset type; absent — cap. */
  firstBaselineRule?: 'ascent' | 'ink' | InddFirstBaselineRule;
  /** Area text from InDesign: the Min first baseline offset, em of `fontSize`. */
  firstBaselineMin?: number;
  /** Photoshop text rules the PSD import composed the box by (`TEXT_LAYOUT_*` in text-layout); absent — legacy. */
  textLayout?: number;
  // ─── ParagraphStyle ───
  firstLineIndent?: number;
  startIndent?: number;
  endIndent?: number;
  spaceBefore?: number;
  spaceAfter?: number;
  autoHyphenate?: boolean;
  hyphenatedWordSize?: number;
  preHyphen?: number;
  postHyphen?: number;
  consecutiveHyphens?: number;
  hyphenationZone?: number;
  /** Photoshop `Language` the words hyphenate by (runs override), from `TEXT_LAYOUT_RUN_LANGUAGE`. */
  language?: number;
  leadingType?: number;
  everyLineComposer?: boolean;
  hanging?: boolean;
  /** Paragraphs with their own indents/spacing/alignment, read from `textLayout` 7 (Rust `RenderBox::paragraph_runs`). */
  paragraphRuns?: readonly TextParagraphRun[];
  // ─── TextStyle (layer-level fallback for runs) ───
  autoKerning?: boolean;
  kerning?: number;
  ligatures?: boolean;
  dLigatures?: boolean;
  noBreak?: boolean;
  strokeColor?: string;
  fillFlag?: boolean;
  strokeFlag?: boolean;
  fillFirst?: boolean;
  outlineWidth?: number;
  yUnderline?: number;
  /** When set, `fontSize`/`lineHeight` are nominal — renderer applies scale via matrix. */
  transform?: TransformMatrix;
  anchor?: TextAnchor;
  /** Optional path-distribution params. When set, renderer distributes glyphs along the SVG path. */
  textPath?: TextPathParams;
  /** Area Type on Shape: замкнутый контур контейнера (нормирован к rect), wrap по пересечению строки с контуром. */
  areaShape?: TextAreaShapeParams;
  /**
   * Objects of the page this frame's text wraps around (InDesign Text Wrap), in the frame's own space. Set by
   * `resolveTextWraps` on the page's boxes, never stored; absent — the text sets as before.
   */
  wrapZones?: readonly WrapZone[];
  /** InDesign «Ignore Text Wrap»: no `wrapZones` for this text; `resolveTextWraps` drops the flag. */
  ignoreTextWrap?: boolean;
  /** The `textWrap.group` tokens of the groups holding this text: their wraps leave it be; `resolveTextWraps` drops it. */
  textWrapGroups?: readonly string[];
  /** Photoshop Warp Text: glyph outlines bent inside a rect-local frame (Rust `text_warp.rs`). */
  warp?: TextWarpParams;
  /** Per-run style overrides. Absent → single-style legacy path (unchanged behavior). */
  runs?: TextRun[];
}

/**
 * Object render box (shapes, decorations, backgrounds)
 */
export interface RenderObjectBox extends RenderBoxBase {
  type: 'object' | 'background';
  pixelData?: string;
  url?: string;
  versions?: {
    thumb?: string;
    /** 1920 screen tier — sits between `medium` and the original; only flat background composites carry one. */
    large?: string;
    medium?: string;
    thumbHash?: string;
  };
  /**
   * Persisted content-rect (trim/crop) within the box, from naturalAligns.content.
   * Absent when the decor hasn't been trimmed — renderer draws the full source untrimmed.
   */
  contentRect?: RenderRect;
  /**
   * Base64 PNG alpha mask applied via destination-in after the object is drawn.
   * Used for non-photo clippers in a Photoshop clipping group (e.g. multiply tint
   * above the photo placeholder) — silhouette comes from the base layer. Carries
   * either an inline data: URL (legacy models, runtime re-bake) or an https URL
   * of the uploaded alpha-only mask.
   */
  maskDataUrl?: string;
  /**
   * The layer this decor is clipped to (Photoshop's clipping mask) — the photo slot of a clip group carries it
   * before it is ever made a frame, so the standing mask cuts it live and the slot slides under the mask.
   */
  maskBaseBoxId?: string;
  /**
   * Recipe to recompute `maskDataUrl` at runtime when a text leaf inside the clipping
   * base group is edited. Set by parser when the bake included text layers — without
   * it the silhouette would stay stale after `ChangeText` on a referenced text box.
   */
  compoundClipBakeRecipe?: CompoundClipBakeRecipe;
}

/**
 * Group render box containing other boxes
 */
export interface RenderGroupBox extends RenderBoxBase {
  type: 'group';
  children: RenderBox[];
  // PhotoBook specific: person association
  personId?: string | null;
  peopleType?: string;
  /** `false` pins the FRAME (the enlarged vignette slot): the card may trade contents but never places.
   *  The card-swap drag reads it so its live preview shows exactly what the drop will commit. */
  draggable?: boolean;
  /** PSD group composite (`params.psdGroup`); absent key = flat group, the scene expands it as before. */
  psdGroup?: PsdGroupComposite;
}

/**
 * Smart object render box
 */
export interface RenderSmartObjectBox extends RenderBoxBase {
  type: 'smartObject';
  pixelData?: string;
  url?: string;
  sourceType?: string;
  thumbHash?: string;
  /** Rendition paths only — the hash lives on `thumbHash` above, which is what both renderers read for a smart object. */
  versions?: {
    thumb?: string;
    medium?: string;
  };
  /** Resolved smart object source url (if sourceRef present in box.params). */
  sourceUrl?: string;
  /** Renditions of the SOURCE, not of the box. They keep their pixel size because `sourceTransform` is
   *  calibrated for the natural source: a tier places right only once its linear part is divided by
   *  `tier.width / sourceNaturalWidth`. */
  sourceVersions?: Pick<ObjectAssetVersions, 'thumb' | 'medium'>;
  /** Affine `[xx, yx, xy, yy, tx, ty]` (column-major) from box.params.transform. */
  sourceTransform?: readonly [number, number, number, number, number, number];
  /** Source natural pixel size (for transform application). */
  sourceNaturalWidth?: number;
  sourceNaturalHeight?: number;
  /** Engine flag — render via source+transform when true and sourceUrl present. */
  useRuntimeSO?: boolean;
  /** Live smart filters, bottom first, run on the source drawn through `sourceTransform` (never on baked pixels).
   *  Set only alongside `sourceUrl`: a stack with an unsupported filter keeps the source off instead. */
  smartFilters?: readonly Exclude<SmartObjectFilter, { kind: 'unsupported' }>[];
  /**
   * Base64 PNG of an alpha-clipping mask. When present, the renderer draws the image, then
   * composites this mask via `destination-in` so the photo takes the silhouette of the
   * original PSD base layer (Photoshop clipping group). Carries either an inline data: URL
   * (legacy models, runtime re-bake) or an https URL of the uploaded alpha-only mask.
   */
  maskDataUrl?: string;
  /**
   * Persisted content-rect (trim/crop) within the box, from naturalAligns.content.
   * Absent when the decor hasn't been trimmed — renderer draws the full source untrimmed.
   */
  contentRect?: RenderRect;
}

/**
 * Vector render box (rectangle/ellipse/roundedRect/line/path с solid/gradient/pattern fill + stroke).
 * Все параметры приходят из DrawableVectorData.vector (без преобразования shape).
 */
export interface RenderVectorBox extends RenderBoxBase {
  type: 'vector';
  vector: VectorBoxParams;
}

/**
 * Adjustment render box (curves / hue-saturation) — a backdrop filter applied at its z-position,
 * scoped to the layer below when clipped, else the whole canvas.
 */
export interface RenderAdjustmentBox extends RenderBoxBase {
  adjustmentResources?: readonly AdjustmentBinaryResource[];
  type: 'adjustment';
  adjustment: AdjustmentBoxParams;
}

/**
 * Union type of all render boxes
 */
export type RenderBox =
  | RenderImageBox
  | RenderTextBox
  | RenderObjectBox
  | RenderGroupBox
  | RenderSmartObjectBox
  | RenderVectorBox
  | RenderAdjustmentBox;

/**
 * Type guard for image box
 */
export function isRenderImageBox(box: RenderBox): box is RenderImageBox {
  return box.type === 'image';
}

/**
 * Type guard for text box
 */
export function isRenderTextBox(box: RenderBox): box is RenderTextBox {
  return box.type === 'text';
}

/**
 * Type guard for object box
 */
export function isRenderObjectBox(box: RenderBox): box is RenderObjectBox {
  return box.type === 'object' || box.type === 'background';
}

/**
 * Type guard for smart object box
 */
export function isRenderSmartObjectBox(box: RenderBox): box is RenderSmartObjectBox {
  return box.type === 'smartObject';
}

/**
 * Type guard for group box
 */
export function isRenderGroupBox(box: RenderBox): box is RenderGroupBox {
  return box.type === 'group';
}

/**
 * Type guard for vector box
 */
export function isRenderVectorBox(box: RenderBox): box is RenderVectorBox {
  return box.type === 'vector';
}

/** Плитке запечённого эффекта (и декору ячейки) — id её хозяина: превью жеста поднимает её вместе с ним. */
export function withBakedTileOwners(box: RenderBox, owners: ReadonlyMap<string, string>): RenderBox {
  const owner = owners.get(box.id);
  const marked: RenderBox = owner ? { ...box, bakedTileOwner: owner } : box;
  if (marked.type !== 'group') return marked;
  const children = marked.children.map(child => withBakedTileOwners(child, owners));
  return children.every((child, i) => child === marked.children[i]) ? marked : { ...marked, children };
}
